import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/src/components/ui/button'
import { Input } from '@/src/components/ui/input'
import { toast } from 'sonner'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { DataTable } from '@/src/components/manage/tables/DataTable'
import { me } from '@/src/api/user'
import { listProjectsByOrganization, deleteProject, rollProjectShareId, copyProject } from '@/src/api/project'
import { useTranslation } from '@/src/i18n/client'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { DeleteAction } from '@/src/components/manage/actions/DeleteAction'
import { CopyAction } from '@/src/components/manage/actions/CopyAction'
import { useAuth } from '@/src/components/AuthContextProvider'
import type { Project } from '@/src/types/project.zod'
import { getProjectColumns } from '@/src/components/manage/projects/projectColumns'
import { PERMISSIONS } from '@/permission.config'
import { ViewAction } from '@/src/components/manage/actions/ViewAction'
import { CreateAction } from '@/src/components/manage/actions/CreateAction'
import { GenericAction } from '@/src/components/manage/actions/GenericAction'


export const Route = createFileRoute('/_manage/manage/projects/')({
  component: ManageProjectsPage,
})

function ManageProjectsPage() {
  const { t } = useTranslation('manage');
  const queryClient = useQueryClient()
  const { user: sessionUser, isAdmin, permissions } = useAuth()
  const navigate = useNavigate()

  // Check permission client-side
  useEffect(() => {
    const hasAccess = permissions.includes(PERMISSIONS.PROJECTS_MANAGE) || permissions.includes('*') || isAdmin
    if (!hasAccess) {
      navigate({ to: '/not-permitted' })
    }
  }, [permissions, isAdmin, navigate])
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const { data: projects, refetch } = useQuery({
    queryKey: ['project', 'listByOrganization', user?.organizationId],
    queryFn: () => listProjectsByOrganization({ data: { orgId: user!.organizationId! } }),
    enabled: !!user?.organizationId,
  })


  const handleRotateShareId = async (projectId: string) => {
    await rollShareIdMutation.mutateAsync(projectId)
    await queryClient.invalidateQueries({ queryKey: ['project', 'listByOrganization'] })
  }

  const columns = getProjectColumns({
    t,
    canRotateShareId: (project) => {
      const ownerId = project.createdBy?.id
      return isAdmin || (!!ownerId && ownerId === sessionUser?.id)
    },
    onRotateShareId: handleRotateShareId,
  });

  const [claimDialog, setClaimDialog] = useState(false)
  const [claimShareId, setClaimShareId] = useState('')
  const [claimPassword, setClaimPassword] = useState('')
  const [claimError, setClaimError] = useState('')


  const deleteProjectMutation = useMutation({
    mutationFn: (id: string) => deleteProject({ data: { id } }),
    onSuccess: () => {
      toast.success(t('projects.projectDeleted'))
      refetch()
    },
    onError: () => toast.error(t('projects.failedToDeleteProject')),
  })

  const rollShareIdMutation = useMutation({
    mutationFn: (projectId: string) => rollProjectShareId({ data: { projectId } }),
  })

  const copyProjectMutation = useMutation({
    mutationFn: (id: string) => copyProject({ data: { id } }),
    onSuccess: () => {
      toast.success(t('projects.projectCopied'))
      queryClient.invalidateQueries({ queryKey: ['project', 'listByOrganization'] })
    },
    onError: (err: Error) => toast.error(err.message || t('projects.failedToCopyProject')),
  })


  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <CreateAction to="/manage/projects/new" label={t('table.createProject')} />
          <GenericAction
            label={t('projects.claimProject')}
            variant="outline"
            onClick={() => setClaimDialog(true)}
          />
        </AdminToolbar.Left>
      </AdminToolbar>
      <div className="bg-background text-foreground">
        <DataTable<Project>
          data={projects || []}
          columns={columns}
          isLoading={userLoading || !projects}
          error={null}
          emptyMessage={t('table.noProjects')}
          actions={(project) => (
            <>
              <ViewAction
                to={`/manage/projects/${project.id}`}
              />
              <EditAction
                to={`/manage/projects/${project.id}/edit`}
              />
              {(project._count?.users ?? 0) > 0 && (
                <CopyAction
                  label={t('projects.copyProject')}
                  onCopy={async () => {
                    await copyProjectMutation.mutateAsync(project.id)
                  }}
                  isPending={copyProjectMutation.isPending}
                />
              )}
              <DeleteAction
                itemName={project.name}
                itemType="project"
                onDelete={async () => {
                  await deleteProjectMutation.mutateAsync(project.id)
                }}
                isPending={deleteProjectMutation.isPending}
              />
            </>
          )}
        />
      </div>
      {/* Claim Project Dialog */}
      {claimDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40">
          <form className="mb-6 p-6 border rounded-md bg-background w-full max-w-md shadow-lg" onSubmit={async (e) => {
            e.preventDefault()
            setClaimError(t('projects.claimNotImplemented'))
          }}>
            <h2 className="font-semibold mb-2">{t('projects.claimProject')}</h2>
            <div className="mb-2">
              <Input value={claimShareId} onChange={e => setClaimShareId(e.target.value)} placeholder={t('table.shareId')} required />
            </div>
            <div className="mb-2">
              <Input type="password" value={claimPassword} onChange={e => setClaimPassword(e.target.value)} placeholder={t('projects.passwordPlaceholder')} required />
            </div>
            {claimError && <div className="text-destructive text-sm mb-2">{claimError}</div>}
            <div className="flex gap-2 mt-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setClaimDialog(false)}>{t('cancel')}</Button>
              <Button type="submit">{t('projects.claim')}</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
